/**
 * Per-element animation easing curves. Each function returns
 * `{ scale, alpha }` so the caller can drive a single ctx.save() →
 * font + fillStyle change without recomputing curves inline. Pulled
 * out so animation tuning lives in one file and the draw modules
 * stay focused on layout / paint order.
 */

/**
 * Big centre-banner scale across its life. `elapsed` is 0 at spawn,
 * 1 at death. Pops in from ~0.45→1.2 over the first 18% of life,
 * settles to 1.0 by ~32%, then holds — the previous "shrink from 1.25
 * to 1.0 over the entire life" felt backwards (banner reads at peak
 * size the moment you can least react, then shrinks while you can).
 */
export function bigBannerScale(elapsed: number): number {
  if (elapsed < 0.18) {
    const p = elapsed / 0.18;
    const eased = 1 - Math.pow(1 - p, 3);
    return 0.45 + 0.75 * eased;
  }
  if (elapsed < 0.32) {
    const p = (elapsed - 0.18) / 0.14;
    return 1.2 - 0.2 * p;
  }
  return 1.0;
}

/**
 * Per-number countdown animation: punch in fast, settle to a stable
 * hold size, then shrink-and-fade just before the next number takes
 * over. `tWithin` is 0 when the renderer first shows this integer
 * (e.g. countdown just crossed from 3.01 → 3.00) and 1 when it's
 * about to flip to the next one.
 */
export function countdownNumberAnim(
  tWithin: number,
  reducedMotion: boolean,
): { scale: number; alpha: number } {
  if (reducedMotion) {
    return { scale: 1, alpha: 1 };
  }
  if (tWithin < 0.15) {
    const p = tWithin / 0.15;
    const eased = 1 - Math.pow(1 - p, 3);
    return { scale: 0.4 + 1.0 * eased, alpha: eased };
  }
  if (tWithin < 0.3) {
    const p = (tWithin - 0.15) / 0.15;
    return { scale: 1.4 - 0.3 * p, alpha: 1 };
  }
  if (tWithin < 0.75) {
    return { scale: 1.1, alpha: 1 };
  }
  const p = Math.min(1, (tWithin - 0.75) / 0.25);
  return { scale: 1.1 - 0.4 * p, alpha: 1 - p };
}

/**
 * "GO!" banner animation across its short life. `t` is 0 at the
 * moment the lead-in ended, 1 when the banner expires. Bigger
 * overshoot than countdown numbers — this is the climax of the
 * count, not just another integer in the sequence.
 */
export function goBannerAnim(
  t: number,
  reducedMotion: boolean,
): { scale: number; alpha: number } {
  if (reducedMotion) {
    return { scale: 1, alpha: t < 0.85 ? 1 : Math.max(0, 1 - (t - 0.85) / 0.15) };
  }
  if (t < 0.2) {
    const p = t / 0.2;
    const eased = 1 - Math.pow(1 - p, 3);
    return { scale: 0.3 + 1.1 * eased, alpha: eased };
  }
  if (t < 0.35) {
    const p = (t - 0.2) / 0.15;
    return { scale: 1.4 - 0.2 * p, alpha: 1 };
  }
  if (t < 0.8) {
    return { scale: 1.2, alpha: 1 };
  }
  const p = (t - 0.8) / 0.2;
  return { scale: 1.2 - 0.3 * p, alpha: 1 - p };
}
